
import type { Bill } from './Bill'
import type Customer from './Customer'
import type { DeliveryNoteCustomField, DeliveryNoteCustomFieldRq } from './DeliveryNoteConfig'
import type Driver from './Driver'
import type Employee from './Employee'

import type { Payload as LoadOrderReferencePayload } from './LoadOrder'
import type Place from './Place'
import { PriceUnit } from './Price'
import type Vehicle from './Vehicle'
import { isPayloadValid } from '@/utils/FormValidation'

export enum ShareDocumentToDriverBy {
  NONE = 'NONE',
  EMAIL = 'EMAIL',
}

export type OperatorRole = 'CARRIER' | 'OPERATOR' | 'SHIPPER'

export function isNonCarrierRole(role?: OperatorRole): boolean {
  return role === 'OPERATOR' || role === 'SHIPPER'
}

export function showContractualShipper(
  deliveryNote: Pick<DeliveryNote, 'operatorRole' | 'contractualShipper'>
): boolean {
  return isNonCarrierRole(deliveryNote.operatorRole) || !!deliveryNote.contractualShipper
}

export function showCarrierSnapshot(
  deliveryNote: Pick<DeliveryNote, 'operatorRole' | 'carrier'>
): boolean {
  return isNonCarrierRole(deliveryNote.operatorRole) || !!deliveryNote.carrier
}

export function hasExternalQrCode(
  deliveryNote: Pick<DeliveryNote, 'externalQrCode'>
): boolean {
  return !!deliveryNote.externalQrCode?.trim()
}

export interface TripDto {
  dateTime: Date;
  amount?: number;
}

export interface Trip {
  dateTime: string;
  amount: number;
}

export type SchemaVersion = 1 | 2

export type DeliveryNoteType = 'DECA' | 'CMR' | 'CONSIGNMENT_NOTE'

export const DELIVERY_NOTE_TYPES: DeliveryNoteType[] = ['DECA', 'CMR', 'CONSIGNMENT_NOTE']

export interface PlaceRef {
  id?: string
  name?: string
}

export interface CustomerRef {
  id?: string
  name?: string
  vatNumber?: string
  address?: string
}

export interface DeliveryNoteDeliveryDto {
  id?: string
  origins: PlaceRef[]
  originCustomers: CustomerRef[]
  destinations: PlaceRef[]
  destinationCustomers: CustomerRef[]
  originPayloads?: PayloadDto[]
  payloads: PayloadDto[]
  originSigned?: boolean
  signed?: boolean
}

export interface DeliveryNoteDelivery {
  id?: string
  origins?: Place[]
  originCustomers?: Customer[]
  destinations?: Place[]
  destinationCustomers?: Customer[]
  originPayloads?: Payload[]
  payloads: Payload[]
  originSigned?: boolean
  signed?: boolean
}

export interface PayloadDto {
  payloadId?: string;
  payload: string;
  unit?: PriceUnit;
  quantity?: number;
  loadOrderId?: string;
  marksAndNumbers?: string;
  numberOfPackages?: number;
  methodOfPacking?: string;
  statisticalNumber?: string;
  volumeM3?: number;
}

export interface Payload {
  id: string;
  payloadId?: string;
  payload: string;
  unit?: PriceUnit;
  quantity?: number;
  loadOrderId?: string;
  marksAndNumbers?: string;
  numberOfPackages?: number;
  methodOfPacking?: string;
  statisticalNumber?: string;
  volumeM3?: number;
}

export interface EmbeddedCompany {
  id: string;
  name: string;
}

export interface LoadOrderReference {
  id: string;
  number: string;
  payloads?: LoadOrderReferencePayload[];
}

export interface SignaturePoint {
  x: number;
  y: number;
  elapsed: number; // ms since the previous point (0 for the first point)
}

export interface SignatureMetadata {
  timestamp?: string;
  ipAddress?: string;
  userAgent?: string;
  geolocation?: {
    latitude: number;
    longitude: number;
  };
  employeeSignaturePoints?: SignaturePoint[]; // reuse SignaturePoint type for points
  customerSignaturePoints?: SignaturePoint[];
  employeeSignatureNotes?: string;
  customerSignatureNotes?: string; customerFullName?: string;
}

export interface ContractualShipper {
  id: string;
  businessName: string;
  vatNumber: string;
  address?: string;
  postCode?: string;
  city?: string;
  state?: string;
  country?: string;
}

export function isDeliveryNoteV2(
  deliveryNote: Pick<DeliveryNote, 'schemaVersion'>
): boolean {
  return deliveryNote.schemaVersion === 2
}

export function allowsStandardDeliveryNotePdf(
  deliveryNote: Pick<DeliveryNote, 'schemaVersion' | 'type'>
): boolean {
  if (!isDeliveryNoteV2(deliveryNote) || deliveryNote.type == null) {
    return true
  }
  return deliveryNote.type !== 'CMR'
}

export function allowsCmrDeliveryNotePdf(
  deliveryNote: Pick<DeliveryNote, 'schemaVersion' | 'type'>
): boolean {
  if (!isDeliveryNoteV2(deliveryNote) || deliveryNote.type == null) {
    return true
  }
  return deliveryNote.type === 'CMR'
}

export function deliveryNoteRequiresSingleRoute(
  type: DeliveryNoteType | null | undefined
): boolean {
  return type === 'CMR' || type === 'CONSIGNMENT_NOTE'
}

export function deliveryNoteAllowsSigning(
  deliveryNote: Pick<DeliveryNote, 'schemaVersion' | 'type'>
): boolean {
  return !(isDeliveryNoteV2(deliveryNote) && deliveryNote.type === 'DECA')
}

function hasNonEmptyPlaceRef(ref: PlaceRef): boolean {
  return !!(ref.id?.trim() || ref.name?.trim())
}

function clampPlaceRefsToSingle(refs: PlaceRef[]): PlaceRef[] {
  const kept = refs.find(hasNonEmptyPlaceRef)
  return kept ? [{ id: kept.id, name: kept.name }] : [{ id: undefined, name: undefined }]
}

export function clampDeliveriesForSingleRoute(
  deliveries: DeliveryNoteDeliveryDto[]
): DeliveryNoteDeliveryDto[] {
  const first = deliveries[0] ?? createEmptyDeliveryDto()
  return [{
    ...first,
    origins: clampPlaceRefsToSingle(first.origins ?? []),
    destinations: clampPlaceRefsToSingle(first.destinations ?? []),
  }]
}

export function joinPlaceNames(places: Array<Pick<Place, 'name'> | undefined> | undefined): string {
  if (!places?.length) {
    return ''
  }
  return places
    .map((place) => place?.name?.trim())
    .filter((name): name is string => !!name)
    .join('; ')
}

export function getDeliveryNoteListOriginLabel(deliveryNote: DeliveryNote): string {
  if (isDeliveryNoteV2(deliveryNote) && deliveryNote.deliveries?.length) {
    return joinPlaceNames(deliveryNote.deliveries[0].origins)
  }
  return deliveryNote.origin?.name ?? ''
}

export function getDeliveryNoteListDestinationLabel(deliveryNote: DeliveryNote): string {
  if (isDeliveryNoteV2(deliveryNote) && deliveryNote.deliveries?.length) {
    return joinPlaceNames(deliveryNote.deliveries[0].destinations)
  }
  return deliveryNote.destination?.name ?? ''
}

export function getDeliveryNoteListPayloads(deliveryNote: DeliveryNote): Payload[] {
  if (isDeliveryNoteV2(deliveryNote) && deliveryNote.deliveries?.length) {
    return deliveryNote.deliveries.flatMap((delivery) => delivery.payloads ?? [])
  }
  return deliveryNote.payloads ?? []
}

export function isDeliveryNoteUncompleted(deliveryNote: DeliveryNote): boolean {
  if (!deliveryNote.customer) {
    return true
  }
  if (isDeliveryNoteV2(deliveryNote) && deliveryNote.deliveries?.length) {
    const originLabel = getDeliveryNoteListOriginLabel(deliveryNote)
    const destinationLabel = getDeliveryNoteListDestinationLabel(deliveryNote)
    const payloads = getDeliveryNoteListPayloads(deliveryNote)
    return !originLabel.trim() || !destinationLabel.trim() || payloads.length === 0
  }
  return !deliveryNote.origin || !deliveryNote.destination
}

export function hasValidDeliveryLine(delivery: DeliveryNoteDeliveryDto): boolean {
  const hasOrigin = delivery.origins.some(
    (origin) => isStringValid(origin.id) || isStringValid(origin.name)
  )
  const hasDestination = delivery.destinations.some(
    (destination) => isStringValid(destination.id) || isStringValid(destination.name)
  )
  const hasPayload = delivery.payloads.some(isPayloadValidForDeliveryLine)
  return hasOrigin && hasDestination && hasPayload
}

export function isPayloadValidForDeliveryLine(payload: PayloadDto): boolean {
  return isPayloadValid(payload)
}

export function areDeliveryNoteDeliveryPayloadsValid(
  deliveries: DeliveryNoteDeliveryDto[] | undefined
): boolean {
  if (!deliveries?.length) {
    return true
  }
  return deliveries.every((delivery) =>
    delivery.payloads.every((payload) => {
      if (payload.quantity == null || payload.quantity <= 0) {
        return true
      }
      return isPayloadValid(payload)
    })
  )
}

export function isDeliveryNoteDeliveriesRequestValid(
  deliveries: DeliveryNoteDeliveryDto[] | undefined
): boolean {
  if (!deliveries?.length) {
    return false
  }
  return deliveries.every(hasValidDeliveryLine) && areDeliveryNoteDeliveryPayloadsValid(deliveries)
}

function isStringValid(value: string | undefined): boolean {
  return !!value?.trim()
}

export function createEmptyDeliveryDto(): DeliveryNoteDeliveryDto {
  return {
    origins: [{ id: undefined, name: undefined }],
    originCustomers: [],
    destinations: [{ id: undefined, name: undefined }],
    destinationCustomers: [],
    payloads: [],
  }
}

/** Clone a delivery line for in-form duplicate; new lines must not inherit signature state. */
export function cloneDeliveryDtoForDuplicate(source: DeliveryNoteDeliveryDto): DeliveryNoteDeliveryDto {
  const originPayloads = source.originPayloads?.length
    ? source.originPayloads.map((payload) => ({ ...payload }))
    : undefined

  return {
    origins: source.origins.map((place) => ({ ...place })),
    originCustomers: (source.originCustomers ?? []).map((customer) => ({ ...customer })),
    destinations: source.destinations.map((place) => ({ ...place })),
    destinationCustomers: (source.destinationCustomers ?? []).map((customer) => ({ ...customer })),
    payloads: source.payloads.map((payload) => ({ ...payload })),
    ...(originPayloads ? { originPayloads } : {}),
  }
}

export function mapDeliveryNoteToDeliveryDtos(deliveryNote: DeliveryNote): DeliveryNoteDeliveryDto[] {
  if (isDeliveryNoteV2(deliveryNote) && deliveryNote.deliveries?.length) {
    return deliveryNote.deliveries.map((delivery) => ({
      id: delivery.id,
      origins: (delivery.origins ?? []).map((place) => ({
        id: place.id,
        name: place.id ? undefined : place.name,
      })),
      originCustomers: (delivery.originCustomers ?? []).map((customer) => ({
        id: customer.id,
        name: customer.id ? undefined : customer.name,
        vatNumber: customer.id ? undefined : customer.vatNumber,
        address: customer.id ? undefined : customer.address,
      })),
      destinations: (delivery.destinations ?? []).map((place) => ({
        id: place.id,
        name: place.id ? undefined : place.name,
      })),
      destinationCustomers: (delivery.destinationCustomers ?? []).map((customer) => ({
        id: customer.id,
        name: customer.id ? undefined : customer.name,
        vatNumber: customer.id ? undefined : customer.vatNumber,
        address: customer.id ? undefined : customer.address,
      })),
      ...(delivery.originPayloads?.length
        ? {
            originPayloads: (delivery.originPayloads ?? []).map((payload) => ({
              payloadId: payload.payloadId,
              payload: payload.payload,
              unit: payload.unit,
              quantity: payload.quantity,
              loadOrderId: payload.loadOrderId,
              marksAndNumbers: payload.marksAndNumbers,
              numberOfPackages: payload.numberOfPackages,
              methodOfPacking: payload.methodOfPacking,
              statisticalNumber: payload.statisticalNumber,
              volumeM3: payload.volumeM3,
            })),
          }
        : {}),
      payloads: (delivery.payloads ?? []).map((payload) => ({
        payloadId: payload.payloadId,
        payload: payload.payload,
        unit: payload.unit,
        quantity: payload.quantity,
        loadOrderId: payload.loadOrderId,
        marksAndNumbers: payload.marksAndNumbers,
        numberOfPackages: payload.numberOfPackages,
        methodOfPacking: payload.methodOfPacking,
        statisticalNumber: payload.statisticalNumber,
        volumeM3: payload.volumeM3,
      })),
      originSigned: delivery.originSigned,
      signed: delivery.signed,
    }))
  }
  return [{
    ...createEmptyDeliveryDto(),
    origins: [{
      id: deliveryNote.origin?.id,
      name: deliveryNote.origin?.id ? undefined : deliveryNote.origin?.name,
    }],
    destinations: [{
      id: deliveryNote.destination?.id,
      name: deliveryNote.destination?.id ? undefined : deliveryNote.destination?.name,
    }],
    originCustomers: deliveryNote.originCustomer
      ? [{
          id: deliveryNote.originCustomer.id,
          name: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.name,
          vatNumber: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.vatNumber,
          address: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.address,
        }]
      : [],
    destinationCustomers: deliveryNote.destinationCustomer
      ? [{
          id: deliveryNote.destinationCustomer.id,
          name: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.name,
          vatNumber: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.vatNumber,
          address: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.address,
        }]
      : [],
    payloads: (deliveryNote.payloads ?? []).map((payload) => ({
      payloadId: payload.payloadId,
      payload: payload.payload,
      unit: payload.unit,
      quantity: payload.quantity,
      loadOrderId: payload.loadOrderId,
      marksAndNumbers: payload.marksAndNumbers,
      numberOfPackages: payload.numberOfPackages,
      methodOfPacking: payload.methodOfPacking,
      statisticalNumber: payload.statisticalNumber,
      volumeM3: payload.volumeM3,
    })),
  }]
}

export interface DeliveryNote {
  id: string;
  companyId: string;
  schemaVersion?: SchemaVersion;
  type?: DeliveryNoteType | null;
  deliveries?: DeliveryNoteDelivery[];
  number: string;
  autogenerateNumber?: boolean;
  date: string;
  customer?: Customer;
  customerToBill?: Customer;
  employee: Employee;
  employeeName?: string;  // Only set if the employee is null
  vehicles: Vehicle[];
  vehiclePlates?: string[];  // Only set if the vehicles are null
  origin?: Place;
  originCustomer?: Customer;
  destination?: Place;
  destinationCustomer?: Customer;
  originPayloads?: Payload[];
  payloads: Payload[];
  notes: string;
  senderInstructions?: string;
  paymentInstructions?: string;
  cashOnDelivery?: string;
  successiveCarriers?: string;
  specialAgreements?: string;
  consignmentNotePrice?: string | null;
  trips: Trip[];
  originSigned?: boolean;
  signed?: boolean;
  bill?: Bill;
  mandatorySign: boolean;
  originMandatorySign?: boolean;
  loadOrders?: LoadOrderReference[];
  originSignatureMetadata?: SignatureMetadata;
  signatureMetadata?: SignatureMetadata;
  manualValidationRequired?: boolean;
  operatorRole?: OperatorRole;
  carrier?: Customer;
  contractualShipper?: ContractualShipper;
  driver?: Driver;
  customFields?: DeliveryNoteCustomField[];
  externalQrCode?: string;
  shareDocumentToDriverBy?: ShareDocumentToDriverBy;
  hideClient?: boolean;
}

export interface DeliveryNoteRequestDeliveriesFields {
  schemaVersion?: SchemaVersion;
  deliveries?: DeliveryNoteDeliveryDto[];
}

export interface DeliveryNoteResponse {
  deliveryNotes: DeliveryNote[];
}

export interface DeliveryNoteRequest extends DeliveryNoteRequestDeliveriesFields {
  id: string;
  type?: DeliveryNoteType | null;
  employeeId?: string;
  driverId?: string;
  number: string;
  autogenerateNumber?: boolean;
  date?: string;
  operatorRole?: OperatorRole;
  customerId?: string;
  carrierId?: string;
  carrierName?: string;
  carrierVatNumber?: string;
  carrierAddress?: string;
  employeeName?: string;
  employeeSurname?: string;
  employeeNif?: string;
  employeeEmail?: string;
  driverName?: string;
  driverSurname?: string;
  driverNif?: string;
  driverEmail?: string;
  customerName?: string;
  customerVatNumber?: string;
  customerAddress?: string;
  customerEmail?: string;
  customerToBillId?: string;
  contractualShipperId?: string;
  contractualShipperBusinessName?: string;
  contractualShipperVatNumber?: string;
  contractualShipperAddress?: string;
  contractualShipperPostCode?: string;
  contractualShipperCity?: string;
  contractualShipperState?: string;
  contractualShipperCountry?: string;
  vehicleIds?: string[];
  vehiclePlates?: string[];
  originId?: string;
  originName?: string;
  originCustomerId?: string;
  originCustomerName?: string;
  originCustomerVatNumber?: string;
  originCustomerAddress?: string;
  destinationId?: string;
  destinationName?: string;
  destinationCustomerId?: string;
  destinationCustomerName?: string;
  destinationCustomerVatNumber?: string;
  destinationCustomerAddress?: string;
  originPayloads?: Payload[];
  payloads: Payload[];
  notes: string;
  senderInstructions?: string;
  paymentInstructions?: string;
  cashOnDelivery?: string;
  successiveCarriers?: string;
  specialAgreements?: string;
  consignmentNotePrice?: string | null;
  trips: Trip[];
  manualValidationDone?: boolean;
  shareDocumentToDriverBy?: ShareDocumentToDriverBy;
  hideClient?: boolean;
  loadOrderIds?: string[];
  customFields?: DeliveryNoteCustomFieldRq[];
}

export interface DeliveryNoteImage {
  name: string;
  data: Blob;
}

export interface DeliveryNoteImages {
  images: DeliveryNoteImage[];
}


export interface BulkUpdateRequest {
  selectAll: boolean
  selectedIds: string[]
  unselectedIds: string[]
  employeeId?: string
  customerId?: string
  vehicleIds?: string[]
  originId?: string
  destinationId?: string
  originCustomerId?: string
  destinationCustomerId?: string
}

const freeTextField = <T extends { id?: string }>(
  entity: T | undefined,
  field: keyof T
): string | undefined => {
  if (!entity || entity.id) {
    return undefined
  }
  const value = entity[field]
  return typeof value === 'string' ? value : undefined
}

function mapLegacyGoodsForV2CopyDelivery(deliveryNote: DeliveryNote): {
  originPayloads: PayloadDto[]
  payloads: PayloadDto[]
} {
  const originPayloads = (deliveryNote.originPayloads ?? []).map((payload) => ({ ...payload }))
  const payloads = (deliveryNote.payloads ?? []).map((payload) => ({ ...payload }))
  const effectivePayloads = payloads.length ? payloads : originPayloads

  return {
    originPayloads,
    payloads: effectivePayloads,
  }
}

export function buildV2CopyDeliveriesFromLegacyDeliveryNote(
  deliveryNote: DeliveryNote
): DeliveryNoteDeliveryDto[] {
  const goods = mapLegacyGoodsForV2CopyDelivery(deliveryNote)

  const delivery: DeliveryNoteDeliveryDto = {
    origins: [{
      id: deliveryNote.origin?.id,
      name: deliveryNote.origin?.id ? undefined : deliveryNote.origin?.name,
    }],
    originCustomers: deliveryNote.originCustomer
      ? [{
          id: deliveryNote.originCustomer.id,
          name: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.name,
          vatNumber: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.vatNumber,
          address: deliveryNote.originCustomer.id ? undefined : deliveryNote.originCustomer.address,
        }]
      : [],
    destinations: [{
      id: deliveryNote.destination?.id,
      name: deliveryNote.destination?.id ? undefined : deliveryNote.destination?.name,
    }],
    destinationCustomers: deliveryNote.destinationCustomer
      ? [{
          id: deliveryNote.destinationCustomer.id,
          name: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.name,
          vatNumber: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.vatNumber,
          address: deliveryNote.destinationCustomer.id ? undefined : deliveryNote.destinationCustomer.address,
        }]
      : [],
    payloads: goods.payloads,
  }

  if (goods.originPayloads.length) {
    delivery.originPayloads = goods.originPayloads
  }

  return [delivery]
}

export function mapDeliveryNoteToCopyDeliveryDtos(deliveryNote: DeliveryNote): DeliveryNoteDeliveryDto[] {
  if (!isDeliveryNoteV2(deliveryNote) || !deliveryNote.deliveries?.length) {
    return buildV2CopyDeliveriesFromLegacyDeliveryNote(deliveryNote)
  }

  return mapDeliveryNoteToDeliveryDtos(deliveryNote).map((delivery) => {
    const originPayloads = (delivery.originPayloads ?? []).map((payload) => ({ ...payload }))
    const payloads = delivery.payloads.map((payload) => ({ ...payload }))
    const effectivePayloads = payloads.length ? payloads : originPayloads

    return {
      origins: delivery.origins.map((place) => ({ ...place })),
      originCustomers: delivery.originCustomers.map((customer) => ({ ...customer })),
      destinations: delivery.destinations.map((place) => ({ ...place })),
      destinationCustomers: delivery.destinationCustomers.map((customer) => ({ ...customer })),
      ...(originPayloads.length ? { originPayloads } : {}),
      payloads: effectivePayloads,
    }
  })
}

export function mapDeliveryNoteToCopyRequest(
  dn: DeliveryNote,
  options?: { customers?: Customer[]; date?: string; v2SchemaEnabled?: boolean }
): DeliveryNoteRequest {
  const v2SchemaEnabled = options?.v2SchemaEnabled ?? true
  const useFirstDelivery = isDeliveryNoteV2(dn) && !!dn.deliveries?.length
  const firstDelivery = useFirstDelivery ? dn.deliveries![0] : undefined
  const deliveries = v2SchemaEnabled ? mapDeliveryNoteToCopyDeliveryDtos(dn) : []
  const v2DeliveriesOnNote = v2SchemaEnabled && (isDeliveryNoteV2(dn) && !!dn.deliveries?.length || deliveries.length > 0)
  const flatPayloads = useFirstDelivery
    ? (firstDelivery!.payloads ?? []).map((payload) => ({ ...payload }))
    : (dn.payloads ?? []).map((payload) => ({ ...payload }))
  const flatOriginPayloads = useFirstDelivery
    ? (firstDelivery!.originPayloads ?? []).map((payload) => ({ ...payload }))
    : (dn.originPayloads ?? []).map((payload) => ({ ...payload }))
  const rootPayloads = flatPayloads
  const originPayloadsSource = flatOriginPayloads.length
    ? flatOriginPayloads
    : rootPayloads

  const baseRequest: DeliveryNoteRequest = {
    id: '',
    employeeId: isNonCarrierRole(dn.operatorRole) ? undefined : dn.employee?.id,
    driverId: isNonCarrierRole(dn.operatorRole) ? dn.driver?.id : undefined,
    employeeName: isNonCarrierRole(dn.operatorRole) ? undefined : (dn.employee && !dn.employee.id ? dn.employee.name : undefined),
    employeeSurname: isNonCarrierRole(dn.operatorRole) ? undefined : (dn.employee && !dn.employee.id ? dn.employee.surname : undefined),
    employeeNif: isNonCarrierRole(dn.operatorRole) ? undefined : (dn.employee && !dn.employee.id ? dn.employee.nif : undefined),
    employeeEmail: isNonCarrierRole(dn.operatorRole) ? undefined : (dn.employee && !dn.employee.id ? dn.employee.email : undefined),
    driverName: isNonCarrierRole(dn.operatorRole) ? (dn.driver && !dn.driver.id ? dn.driver.name : undefined) : undefined,
    driverSurname: isNonCarrierRole(dn.operatorRole) ? (dn.driver && !dn.driver.id ? dn.driver.surname : undefined) : undefined,
    driverNif: isNonCarrierRole(dn.operatorRole) ? (dn.driver && !dn.driver.id ? dn.driver.nif : undefined) : undefined,
    driverEmail: isNonCarrierRole(dn.operatorRole) ? (dn.driver && !dn.driver.id ? dn.driver.email : undefined) : undefined,
    number: '',
    date: options?.date,
    customerId: dn.customer?.id,
    customerToBillId: dn.customerToBill?.id,
    ...mapDeliveryNoteEmbeddedToRequest(dn),
    vehicleIds: dn.vehicles?.length ? dn.vehicles.map((vehicle) => vehicle.id) : [],
    vehiclePlates: dn.vehiclePlates?.length ? [...dn.vehiclePlates] : undefined,
    originId: useFirstDelivery ? firstDelivery!.origins?.[0]?.id : dn.origin?.id,
    originCustomerId: useFirstDelivery
      ? firstDelivery!.originCustomers?.[0]?.id
      : dn.originCustomer?.id,
    destinationId: useFirstDelivery ? firstDelivery!.destinations?.[0]?.id : dn.destination?.id,
    destinationCustomerId: useFirstDelivery
      ? firstDelivery!.destinationCustomers?.[0]?.id
      : dn.destinationCustomer?.id,
    originPayloads: v2DeliveriesOnNote ? [] : originPayloadsSource.map((payload) => ({ ...payload })),
    payloads: v2DeliveriesOnNote ? [] : rootPayloads,
    notes: dn.notes,
    trips: dn.trips?.map((trip) => ({ ...trip })) ?? [],
    autogenerateNumber: dn.autogenerateNumber,
    operatorRole: dn.operatorRole,
    carrierId: dn.carrier?.id,
    contractualShipperId: resolveContractualShipperIdForRequest(dn, options?.customers ?? []),
    customFields: mapCustomFieldsToRequest(dn.customFields),
    shareDocumentToDriverBy: isNonCarrierRole(dn.operatorRole)
      ? (dn.shareDocumentToDriverBy ?? ShareDocumentToDriverBy.NONE)
      : undefined,
    hideClient: isNonCarrierRole(dn.operatorRole) ? (dn.hideClient ?? false) : undefined,
    consignmentNotePrice: dn.consignmentNotePrice?.trim() || undefined,
  }

  if (v2SchemaEnabled) {
    return {
      ...baseRequest,
      schemaVersion: 2,
      type: dn.type ?? 'DECA',
      deliveries,
    }
  }

  return baseRequest
}

export const mapDeliveryNoteEmbeddedToRequest = (deliveryNote: DeliveryNote) => ({
  customerName: freeTextField(deliveryNote.customer, 'name'),
  customerVatNumber: freeTextField(deliveryNote.customer, 'vatNumber'),
  customerAddress: freeTextField(deliveryNote.customer, 'address'),
  customerEmail: freeTextField(deliveryNote.customer, 'email'),
  carrierName: freeTextField(deliveryNote.carrier, 'name'),
  carrierVatNumber: freeTextField(deliveryNote.carrier, 'vatNumber'),
  carrierAddress: freeTextField(deliveryNote.carrier, 'address'),
  contractualShipperBusinessName: freeTextField(deliveryNote.contractualShipper, 'businessName'),
  contractualShipperVatNumber: freeTextField(deliveryNote.contractualShipper, 'vatNumber'),
  contractualShipperAddress: freeTextField(deliveryNote.contractualShipper, 'address'),
  contractualShipperPostCode: freeTextField(deliveryNote.contractualShipper, 'postCode'),
  contractualShipperCity: freeTextField(deliveryNote.contractualShipper, 'city'),
  contractualShipperState: freeTextField(deliveryNote.contractualShipper, 'state'),
  contractualShipperCountry: freeTextField(deliveryNote.contractualShipper, 'country'),
  originName: freeTextField(deliveryNote.origin, 'name'),
  originCustomerName: freeTextField(deliveryNote.originCustomer, 'name'),
  originCustomerVatNumber: freeTextField(deliveryNote.originCustomer, 'vatNumber'),
  originCustomerAddress: freeTextField(deliveryNote.originCustomer, 'address'),
  destinationName: freeTextField(deliveryNote.destination, 'name'),
  destinationCustomerName: freeTextField(deliveryNote.destinationCustomer, 'name'),
  destinationCustomerVatNumber: freeTextField(deliveryNote.destinationCustomer, 'vatNumber'),
  destinationCustomerAddress: freeTextField(deliveryNote.destinationCustomer, 'address'),
})

export const resolveContractualShipperIdForRequest = (
  source: { companyId?: string; contractualShipper?: { id?: string } },
  customers: Array<{ id: string }>
): string | undefined => {
  const contractualShipperId = source.contractualShipper?.id
  if (!contractualShipperId || contractualShipperId === source.companyId) {
    return undefined
  }
  return customers.some((customer) => customer.id === contractualShipperId)
    ? contractualShipperId
    : undefined
}

export const mapLoadOrderIdsFromDeliveryNote = (deliveryNote: DeliveryNote): string[] | undefined =>
  deliveryNote.loadOrders?.map((loadOrder) => loadOrder.id)

export const buildCustomFieldsRequest = (
  valuesById: Record<string, string>,
  definitions: DeliveryNoteCustomField[]
): DeliveryNoteCustomFieldRq[] =>
  definitions
    .filter((field) => field.id)
    .map((field) => ({
      id: field.id!,
      value: valuesById[field.id!] ?? '',
    }))

export const resolveCustomFieldDefinitions = (
  liveConfig?: DeliveryNoteCustomField[],
  snapshot?: DeliveryNoteCustomField[]
): DeliveryNoteCustomField[] => {
  if (snapshot?.length) {
    return snapshot.map(({ value: _value, ...definition }) => definition)
  }
  return liveConfig ?? []
}

export interface MergeCustomFieldValuesSources {
  snapshot?: DeliveryNoteCustomField[]
  requestFields?: DeliveryNoteCustomFieldRq[]
}

export const mergeCustomFieldValues = (
  definitions: DeliveryNoteCustomField[],
  sources?: MergeCustomFieldValuesSources
): Record<string, string> => {
  const values: Record<string, string> = {}
  const valuesById = new Map<string, string>()

  for (const field of sources?.requestFields ?? []) {
    if (field.id) {
      valuesById.set(field.id, field.value ?? '')
    }
  }

  for (const field of sources?.snapshot ?? []) {
    if (field.id) {
      valuesById.set(field.id, field.value ?? '')
    }
  }

  for (const definition of definitions) {
    if (!definition.id) {
      continue
    }
    values[definition.id] = valuesById.get(definition.id) ?? ''
  }

  return values
}

export const mapCustomFieldsToRequest = (
  customFields?: DeliveryNoteCustomField[]
): DeliveryNoteCustomFieldRq[] | undefined => {
  if (!customFields?.length) {
    return undefined
  }

  return customFields
    .filter((field) => field.id)
    .map((field) => ({
      id: field.id!,
      value: field.value ?? '',
    }))
}